// =============================================================================
// app/src/paginas/ContasPagarBoleto.tsx — cadastrar a conta pelo boleto.
//
// Pedido do dono (18/09/2026): "só com a imagem do boleto/pdf eu consigo
// cadastrar? ... coletar as informações, verificar se já tem o código de
// barras/a conta para não haver duplicidade".
//
// O caminho, na ordem em que a tela anda:
//   1. escolhe a foto, a digitalização do iPhone ou o PDF do boleto;
//   2. o aparelho lê as BARRAS (lerBoletoArquivo — nada sai do celular, e não
//      custa nada por leitura);
//   3. ANTES de abrir formulário, confere a repetição:
//        - o mesmo código já está numa conta → para aqui e mostra qual;
//        - o mesmo título já PAGO (2ª via de boleto quitado) → para também;
//        - há conta que parece ser esta — lançada sem código com o mesmo
//          valor, a conta fixa do mês ainda "a definir", ou a via antiga do
//          mesmo título → pergunta se é ela; se for, o código (e o boleto) vão
//          para ela, com a data e o valor do boleto, e nada se cria;
//   4. senão, abre o formulário de sempre já preenchido: valor e vencimento do
//      código, e descrição/categoria do boleto anterior da mesma conta de
//      cobrança, quando houver.
// Nada é gravado sem o dono tocar em "Salvar" ou "É esta".
// =============================================================================
import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ContaPagar } from '@core/types';
import Aviso from '../componentes/Aviso';
import { dadosDoCodigo, formatarLinha } from '../lib/codigoBarras';
import { prepararComprovante } from '../lib/comprovante';
import { salvarCodigoBarrasConta, salvarComprovanteConta, salvarContaECodigo } from '../lib/consultas';
import { valorDaConta } from '../lib/contasPagar';
import { preenchimentoSugerido, sugerirDoBoleto } from '../lib/sugestao';
import { brl, hojeISO } from '../lib/formato';
import { lerBoletoDoArquivo, type ResultadoDaLeitura } from '../lib/lerBoletoArquivo';
import { mensagemDeErro } from '../lib/supabase';
import type { PreenchidoPeloBoleto } from './ContasPagarFormularios';

const dataBR = (iso: string) => iso.split('-').reverse().join('/');

const MOTIVO: Record<Exclude<ResultadoDaLeitura, { ok: true }>['motivo'], string> = {
  'sem-codigo': 'Não consegui ler o código de barras com segurança. Ele precisa aparecer inteiro e nítido — tente outra foto, com a folha reta e bem iluminada, ou digite os números.',
  'pdf-sem-foto': 'Este PDF não tem foto: é o boleto que o banco gera. Abra o PDF, copie a linha digitável (os números em cima das barras) e cole no campo do código.',
  'pdf-sem-codigo': 'Não consegui ler o código de barras deste PDF com segurança. Se for o PDF do banco, copie a linha digitável dele e cole no campo do código; se for digitalização, tente de novo com o código inteiro e nítido.',
  formato: 'Escolha uma foto ou um PDF do boleto.',
};

export function CadastroPeloBoleto({ contas, aoFechar, aoPreencher, aoMudar }: {
  contas: ContaPagar[];
  aoFechar: () => void;
  /** Abre o formulário da conta nova com o que o boleto disse. */
  aoPreencher: (p: PreenchidoPeloBoleto) => void;
  /** Uma conta existente ganhou código/anexo: a lista recarrega. */
  aoMudar: () => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [avisoDoAnexo, setAvisoDoAnexo] = useState<string | null>(null);

  const ler = useMutation({
    mutationFn: (f: File) => lerBoletoDoArquivo(f),
    onSuccess: (r) => setEscolhida(r.ok && r.leituras.length === 1 ? r.leituras[0]!.linha : null),
  });
  const resultado = ler.data;

  const hoje = hojeISO();
  // O motor mora em lib/sugestao.ts desde 28/09/2026, e a tela do LOTE chama o
  // mesmo. Enquanto ele era um useMemo daqui, a outra tela reimplementou uma
  // versão mutilada e as duas divergiram em quatro pontos — ver o cabeçalho de
  // lá. Aqui o comportamento não muda em nada.
  const analise = useMemo(
    () => (escolhida ? sugerirDoBoleto(escolhida, contas, hoje) : null),
    [escolhida, contas, hoje],
  );

  // "É esta": o código (e o boleto, se a conta ainda não tem anexo) vão para
  // a conta que já existia, com a DATA e o VALOR do boleto — o sistema sabe
  // os certos pelo código, e a conta com o dia errado avisaria errado. Conta
  // paga só ganha o código: o que foi pago não se mexe.
  const usarExistente = useMutation({
    mutationFn: async (c: ContaPagar) => {
      if (!escolhida || !analise) return null;
      // O anexo primeiro: se ele não servir, o código vai do mesmo jeito e a
      // tela diz o que faltou (antes, o erro do anexo travava tudo).
      let anexo: Awaited<ReturnType<typeof prepararComprovante>> | null = null;
      let falhaDoAnexo: string | null = null;
      if (arquivo && !c.boleto) {
        try { anexo = await prepararComprovante(arquivo); } catch (e) { falhaDoAnexo = mensagemDeErro(e); }
      }
      if (c.pago_em) {
        await salvarCodigoBarrasConta({ id: c.id, codigo: escolhida });
      } else {
        const d = analise.dados;
        await salvarContaECodigo({
          id: c.id, descricao: c.descricao, categoria: c.categoria,
          valorPrevisto: c.valor_previsto ?? d.valor,
          valorReal: d.valor ?? c.valor_real,
          vencimento: d.vencimento ?? c.vencimento,
          fornecedorId: c.fornecedor_id, observacao: c.observacao, codigo: escolhida,
        });
      }
      // O ENVIO do anexo também pode falhar depois de o código ter ido: a
      // falha vira aviso, não erro — senão a lista recarrega, a tela passa a
      // dizer "já cadastrado" e ninguém fica sabendo que o boleto não subiu.
      if (anexo) {
        try {
          await salvarComprovanteConta({
            contaId: c.id, base64: anexo.base64, contentType: anexo.contentType, nome: anexo.nome,
            tipo: 'boleto',
          });
        } catch (e) {
          falhaDoAnexo = mensagemDeErro(e);
        }
      }
      return falhaDoAnexo;
    },
    onSuccess: (falha) => {
      aoMudar();
      if (falha) setAvisoDoAnexo(`O código foi para a conta, mas o boleto não foi anexado: ${falha}`);
      else aoFechar();
    },
    onError: () => aoMudar(),
  });

  const preencher = () => {
    if (!escolhida || !analise) return;
    const s = preenchimentoSugerido(analise);
    aoPreencher({
      descricao: s.descricao,
      categoria: s.categoria,
      fornecedorId: s.fornecedorId,
      valor: analise.dados.valor,
      vencimento: analise.dados.vencimento,
      codigo: escolhida,
      arquivo,
      palpites: s.palpites,
      porque: s.porque,
    });
  };

  const ocupado = ler.isPending || usarExistente.isPending;

  /** O que o "é esta" vai mudar na conta — dito no botão, antes do toque. */
  const mudancas = (c: ContaPagar) => {
    if (!analise || c.pago_em) return [];
    const d = analise.dados;
    const m: string[] = [];
    if (d.vencimento && d.vencimento !== c.vencimento) m.push(`vencimento ${dataBR(c.vencimento)} → ${dataBR(d.vencimento)}`);
    const v = valorDaConta(c);
    if (d.valor !== null && (v === null || Math.abs(v - d.valor) >= 0.005)) {
      m.push(`valor ${v === null ? 'a definir' : brl(v)} → ${brl(d.valor)}`);
    }
    return m;
  };

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">Nova conta pelo boleto</h2>
      <input ref={entrada} type="file" accept="image/*,application/pdf" className="hidden"
             onChange={(e) => {
               const f = e.target.files?.[0];
               e.target.value = '';
               if (!f) return;
               setArquivo(f);
               setEscolhida(null);
               setAvisoDoAnexo(null);
               usarExistente.reset();
               ler.mutate(f);
             }} />
      <p className="text-xs text-mute">
        Foto do boleto, a digitalização do iPhone ou o PDF. O sistema lê o código de
        barras aqui no aparelho e confere se a conta já está lançada antes de criar outra.
      </p>
      <button type="button" className="botao-primario w-full py-2 text-sm"
              disabled={ocupado} onClick={() => entrada.current?.click()}>
        {ler.isPending ? 'Lendo o código de barras…' : arquivo ? 'Escolher outro arquivo' : 'Escolher foto ou PDF do boleto'}
      </button>
      {arquivo && !ler.isPending && <p className="text-xs text-dim">Arquivo: {arquivo.name}</p>}

      {ler.error ? <Aviso tipo="erro">{mensagemDeErro(ler.error)}</Aviso> : null}
      {resultado && !resultado.ok && (
        <>
          <Aviso tipo="alerta">{MOTIVO[resultado.motivo]}</Aviso>
          {resultado.motivo !== 'formato' && (
            <button type="button" className="botao-secundario w-full py-2 text-sm"
                    onClick={() => aoPreencher({
                      descricao: '', categoria: 'fornecedores', fornecedorId: null,
                      valor: null, vencimento: null, codigo: '', arquivo,
                    })}>
              Digitar a conta, com este arquivo anexado
            </button>
          )}
        </>
      )}

      {/* Duas parcelas na mesma folha: o dono escolhe qual está lançando. */}
      {resultado?.ok && resultado.leituras.length > 1 && !escolhida && (
        <div className="space-y-2">
          <p className="text-sm">Este arquivo tem {resultado.leituras.length} boletos. Qual você está lançando?</p>
          {resultado.leituras.map((l) => {
            const d = dadosDoCodigo(l.linha, hoje);
            return (
              <button key={l.linha} type="button" className="botao-secundario w-full justify-between py-2 text-sm"
                      onClick={() => setEscolhida(l.linha)}>
                <span>{d.quem}</span>
                <span className="numero">
                  {d.valor !== null ? brl(d.valor) : 'sem valor'}{d.vencimento ? ` · ${dataBR(d.vencimento)}` : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {escolhida && analise && (
        <div className="space-y-3">
          <div className="rounded-[0.625rem] border border-edge bg-panel-alt p-3 text-sm">
            <p className="rotulo">Lido do boleto</p>
            <p>
              {analise.dados.quem}
              {analise.dados.valor !== null ? <> · <strong className="numero font-medium">{brl(analise.dados.valor)}</strong></> : ''}
              {analise.dados.vencimento ? ` · vence ${dataBR(analise.dados.vencimento)}` : ' · sem data no código'}
            </p>
            <p className="numero mt-1 break-all text-xs text-mute">{formatarLinha(escolhida)}</p>
            {analise.quem && (
              <p className="mt-1 text-xs text-mute">
                <span className="ponto mr-1.5 align-middle text-ok" aria-hidden="true" />
                Mesma conta de cobrança de {analise.quem.vezes} boleto{analise.quem.vezes > 1 ? 's' : ''} de{' '}
                <strong>{analise.quem.conta.descricao}</strong>.
              </p>
            )}
          </div>

          {avisoDoAnexo ? (
            <>
              <Aviso tipo="alerta">{avisoDoAnexo}</Aviso>
              <button type="button" className="botao-secundario w-full py-2 text-sm" onClick={aoFechar}>Fechar</button>
            </>
          ) : analise.mesmoCodigo || analise.tituloPago ? (
            <>
              {(() => {
                const c = (analise.mesmoCodigo ?? analise.tituloPago)!;
                return (
                  <Aviso tipo="erro">
                    {analise.mesmoCodigo ? 'Este boleto já está cadastrado' : 'Este é outra via de um boleto que já está cadastrado'}:{' '}
                    <strong>{c.descricao}</strong>, vence {dataBR(c.vencimento)}
                    {valorDaConta(c) !== null ? `, ${brl(valorDaConta(c))}` : ''}
                    {c.pago_em ? ` — já foi PAGO em ${dataBR(c.pago_em)}` : ''}.
                    {c.pago_em ? ' Não pague de novo.' : ' Não lance de novo.'}
                  </Aviso>
                );
              })()}
              <button type="button" className="botao-secundario w-full py-2 text-sm" onClick={aoFechar}>Fechar</button>
            </>
          ) : analise.candidatas.length > 0 ? (
            <div className="space-y-2">
              <Aviso tipo="alerta">
                {analise.jaPaga.length
                  ? 'Parece ser uma conta que JÁ FOI PAGA, com o mesmo valor. Se for, não pague de novo: o código vai só para registro.'
                  : <>Parece ser uma conta que já está lançada. Se for, o código
                    {arquivo ? ' e o boleto vão' : ' vai'} para ela e nenhuma conta nova é criada.</>}
              </Aviso>
              {analise.candidatas.slice(0, 4).map((c) => {
                const m = mudancas(c);
                return (
                  <button key={c.id} type="button"
                          className="botao-secundario w-full flex-col items-start gap-0.5 py-2 text-left text-sm"
                          disabled={ocupado}
                          onClick={() => usarExistente.mutate(c)}>
                    <span>
                      É esta: <strong>{c.descricao}</strong>
                      {analise.reemitidas.includes(c) ? ' (a via anterior deste boleto)' : ''}
                    </span>
                    <span className="text-xs font-normal text-mute">
                      {valorDaConta(c) !== null ? brl(valorDaConta(c)) : 'valor a definir'} · vence {dataBR(c.vencimento)}
                      {c.recorrencia_id ? ' · conta fixa' : ''}
                      {c.pago_em ? ` · paga em ${dataBR(c.pago_em)}` : ''}
                      {c.boleto ? ' · já tem boleto anexado (fica o que está)' : ''}
                    </span>
                    {m.length > 0 && (
                      <span className="text-xs font-normal text-alerta">Vai corrigir: {m.join(' · ')}</span>
                    )}
                  </button>
                );
              })}
              {usarExistente.error ? <Aviso tipo="erro">{mensagemDeErro(usarExistente.error)}</Aviso> : null}
              <button type="button" className="botao-primario w-full py-2 text-sm" disabled={ocupado} onClick={preencher}>
                É outra conta — cadastrar nova
              </button>
            </div>
          ) : (
            <button type="button" className="botao-primario w-full py-2 text-sm" disabled={ocupado} onClick={preencher}>
              Continuar: conferir e salvar a conta
            </button>
          )}
        </div>
      )}

      <button type="button" className="botao-secundario w-full py-2 text-sm" disabled={usarExistente.isPending}
              onClick={aoFechar}>
        Voltar
      </button>
    </div>
  );
}
